import React, { useEffect, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router";
import debounce from "lodash.debounce";
import PropTypes from "prop-types";
import styled from "@emotion/styled";
import { RESOURCE_LINKS_MAPPING } from "constants/index";
import { useLinks, useOmnisearch } from "hooks";
import { colors } from "style";
import Modal, { MODAL_SIZE } from "components/Modal";
import { ICON_POSITION, ICON_SIZE, ICON_TYPE, withIcon } from "components/Icon";
import { TextInput } from "components/Form";
import { Link } from "components/Links";

/**
 * Omnisearch Modal Component
 *
 * @param {Boolean}   show
 * @param {Function}  onClose
 */
const OmnisearchModal = ({ show, onClose }) => {
  const navigate = useNavigate();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [queryString, setQueryString] = useState();
  const [debouncedQuery, setDebouncedQuery] = useState();
  const links = useLinks();
  const resourceLinks = useMemo(() => RESOURCE_LINKS_MAPPING(links), [links]);
  const debouncedSetQuery = useMemo(() => debounce((val) => setDebouncedQuery(val), 500), []);

  const { loading, error, data } = useOmnisearch({
    queryString: debouncedQuery,
    limit: 10,
  });

  const handleSearchInputChange = (value) => {
    setQueryString(value);
    debouncedSetQuery(value);
  };

  /**
   * @description Cleanup debounced function on unmount
   */
  useEffect(() => {
    return () => {
      debouncedSetQuery.cancel();
    };
  }, [debouncedSetQuery]);

  /**
   * @description Reset selected index when data changes
   */
  useEffect(() => {
    setSelectedIndex(0);
  }, [data]);

  /**
   * @description Handle keyboard navigation and selection
   */
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!debouncedQuery || !data?.length) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % data.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + data.length) % data.length);
      } else if (e.key === "Enter") {
        e.preventDefault();
        const selected = data[selectedIndex];
        if (selected) {
          const href = resourceLinks[selected.resourceType](selected.urlResourceId);
          onClose(); // close modal
          navigate(href);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [debouncedQuery, data, selectedIndex, resourceLinks, onClose]);

  return (
    <Modal
      show={show}
      onClose={onClose}
      showSave={false}
      size={MODAL_SIZE.medium}
      secondaryButtonMessageId="Global.Close"
      title={<FormattedMessage id="Omnisearch.Modal.Title" />}
      centered={false}
    >
      <SearchWrapper>
        <SearchInput
          type="search"
          placeholder="Search..."
          aria-label="Omnisearch"
          value={queryString}
          onChange={(_name, value) => handleSearchInputChange(value)}
          autoFocus
        />

        <ResultsSection role="list" aria-live="polite">
          {!debouncedQuery && (
            <ResultsMessage>
              <FormattedMessage id="Omnisearch.TypeToSearch" />
            </ResultsMessage>
          )}

          {loading && (
            <ResultsMessage>
              <FormattedMessage id="Omnisearch.Loading" />
            </ResultsMessage>
          )}

          {error && (
            <ResultsMessage>
              <FormattedMessage id="Omnisearch.Error" />
            </ResultsMessage>
          )}

          {debouncedQuery && !loading && !error && !data.length && (
            <ResultsMessage>
              <FormattedMessage id="Omnisearch.NoResults" />
            </ResultsMessage>
          )}

          {data?.map((result, index) => {
            const isSelected = index === selectedIndex;
            return (
              <ResultLink
                to={resourceLinks[result.resourceType](result.urlResourceId)}
                key={index}
                routerEnabled
                onClick={onClose}
              >
                <ResultCard isSelected={isSelected} role="listitem">
                  <ResultTopRow>
                    <ResultName>{result.name}</ResultName>
                    <TypeBadge>{result.resourceType}</TypeBadge>
                  </ResultTopRow>
                  <ResultMeta>{result.metadataDisplay}</ResultMeta>
                </ResultCard>
              </ResultLink>
            );
          })}
        </ResultsSection>
      </SearchWrapper>
    </Modal>
  );
};

const SearchInput = withIcon(TextInput, {
  type: ICON_TYPE.search,
  size: ICON_SIZE.medium,
  position: ICON_POSITION.overlay,
  order: 0,
});

const SearchWrapper = styled.div`
  width: 100%;
  position: relative;
`;

const ResultsSection = styled.div`
  margin-top: 2rem;
  padding: 1rem 0;
  border-top: 1px solid ${colors.grayAnatomyLight3};
`;

const ResultLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  display: block;

  &:hover {
    text-decoration: none;
  }
`;

const ResultCard = styled.div`
  padding: 1rem 1.5rem;
  cursor: pointer;
  transition: background 0.2s ease;
  border-bottom: 1px solid ${colors.grayAnatomyLight3};
  background: ${({ isSelected }) => (isSelected ? colors.purpleRainLight8 : "transparent")};

  &:hover {
    background: ${colors.purpleRainLight9};
  }
`;

const ResultTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ResultName = styled.div`
  font-size: 1.5rem;
  font-weight: 500;
`;

const ResultMeta = styled.div`
  font-size: 1.3rem;
  color: ${colors.purpleRainLight4};
  margin-top: 0.2rem;
`;

const TypeBadge = styled.div`
  font-size: 1rem;
  text-transform: uppercase;
  background: ${colors.purpleRainLight5};
  color: ${colors.purpleRainDark1};
  padding: 0.2rem 0.6rem;
  border-radius: 0.4rem;
  white-space: nowrap;
`;

const ResultsMessage = styled.div`
  padding: 1rem 1.5rem;
  color: ${colors.grayAnatomyLight1};
  font-style: italic;
`;

OmnisearchModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default OmnisearchModal;
